import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import OTPInput from './OTPInput';

const EmailVerification = ({ email, onVerificationSuccess, onBack }) => {
    const { verifyOTP, resendVerification, verificationLoading } = useAuth();
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);

    // Cooldown timer for resend button
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleVerifyOTP = async () => {
        if (otp.length !== 6) {
            setError('Please enter the complete 6-digit code');
            return;
        }

        setIsVerifying(true);
        setError('');
        setSuccess('');

        try {
            const result = await verifyOTP(email, otp);

            if (result.success) {
                setSuccess('Email verified successfully! Redirecting...');
                setTimeout(() => {
                    onVerificationSuccess && onVerificationSuccess();
                }, 1500);
            } else {
                setError(result.error || 'Verification failed. Please try again.');
            }
        } catch (error) {
            setError('Verification failed. Please try again.');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return;

        setError('');
        setSuccess('');

        try {
            const result = await resendVerification(email);

            if (result.success) {
                setSuccess('Verification code sent! Please check your email.');
                setResendCooldown(60); // 60 second cooldown
            } else {
                setError(result.error || 'Failed to resend code. Please try again.');
            }
        } catch (error) {
            setError('Failed to resend code. Please try again.');
        }
    };

    const handleOTPChange = (value) => {
        setOtp(value);
        setError(''); // Clear error when user starts typing
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && otp.length === 6) {
            handleVerifyOTP();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center px-4">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        Verify Your Email
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        We've sent a 6-digit verification code to
                    </p>
                    <p className="text-sm font-medium text-orange-600">
                        {email}
                    </p>
                </div>

                <div className="bg-white py-8 px-6 shadow-lg rounded-lg border">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enter verification code
                            </label>
                            <OTPInput
                                value={otp}
                                onChange={handleOTPChange}
                                onKeyPress={handleKeyPress}
                                disabled={isVerifying || verificationLoading}
                            />
                        </div>

                        {error && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-800">{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {success && (
                            <div className="rounded-md bg-green-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-green-800">{success}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <button
                                onClick={handleVerifyOTP}
                                disabled={otp.length !== 6 || isVerifying || verificationLoading}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isVerifying || verificationLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Verifying...
                                    </div>
                                ) : (
                                    'Verify Email'
                                )}
                            </button>

                            <div className="text-center">
                                <p className="text-sm text-gray-600">
                                    Didn't receive the code?{' '}
                                    <button
                                        onClick={handleResendCode}
                                        disabled={resendCooldown > 0 || verificationLoading}
                                        className="font-medium text-orange-600 hover:text-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {resendCooldown > 0 ? (
                                            `Resend in ${resendCooldown}s`
                                        ) : (
                                            'Resend code'
                                        )}
                                    </button>
                                </p>
                            </div>

                            {onBack && (
                                <div className="text-center">
                                    <button
                                        onClick={onBack}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                        ← Back to login
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        Having trouble? Check your spam folder or contact support.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification; 